import type { BillingPlanTier } from '@core/billing-types';
import type { EEOKind } from '@core/eeo-types';
import { SalutationFieldsConfig } from '@core/salutation-fields-types';
import {
  AutomationActionType,
  AutomationConditionType,
  AutomationTriggerType,
} from '@core/workflow-automations-types';

export enum FeatureCode {
  AcquisitionPaidCampaigns = 'acquisition_paid_campaigns',
  AdjustableRoles = 'adjustable_roles',
  CandidateAnonymization = 'candidate_anonymization',
  CustomisableReports = 'customisable_reports',
  AuditLogs = 'audit_logs',
  AutomatedActions = 'automated_actions',
  Careers = 'careers_site',
  CustomDomain = 'custom_domain',
  DefaultProfileFields = 'default_profile_fields',
  EEO = 'eeo',
  EvaluationForms = 'evaluation_forms',
  FairEvaluations = 'fair_evaluations',
  GDPRAutomations = 'gdpr_automations',
  JobScheduler = 'job_scheduler',
  KnockoutQuestions = 'knockout_questions',
  Locations = 'locations',
  MeetingRooms = 'meeting_rooms',
  Multilang = 'multilang',
  NotificationsPerRole = 'notifications_per_role',
  OpenQuestions = 'open_questions',
  OverdueCandidates = 'overdue_candidates',
  Pipelines = 'pipelines',
  ProfilePrint = 'profile_print',
  Questionnaires = 'questionnaires',
  OfferLetters = 'offer_letters',
  Referrals = 'referrals',
  Requisitions = 'requisitions',
  Scheduler = 'scheduler',
  Security = 'security',
  SendLater = 'send_later',
  SourcingExtension = 'sourcing_extension',
  SSO = 'sso',
  Visibility = 'visibility',
  Webhooks = 'webhooks',
  Dashboards = 'dashboards',
  JobTemplates = 'offer_templates',
  CustomFields = 'dynamic_fields',
  JobAiTranslation = 'ai_translate',
  AiWriter = 'ai_writer',
  AiJobContent = 'ai_job_content',
  EvaluationInsights = 'evaluation_insights',
  JobDashboard = 'job_dashboard',
  WorkflowAutomations = 'workflow_automations',
  AiCandidateScreening = 'ai_candidate_screening',
  AiCandidateRecommendations = 'ai_candidate_recommendations',
  JobPageLayouts = 'job_page_layouts',
  CandidateSalutationFields = 'salutation_fields',
}

type Base = {
  enabled: boolean;
  entitled: boolean;
  availableFrom: BillingPlanTier | null;
};

type EmptyConfig = {};

export type LocationsConfig = {
  limit: number | null;
};

export type CustomFieldsConfig = {
  limit: number | null;
};

export type EEOConfig = {
  dataRetentionDays: number;
  emailBodyHtml: string;
  emailSubject: string;
  kind: EEOKind;
};

export type GDPRConfig = {
  hideCandidatesNames: boolean;
};

export type MultilangConfig = {
  defaultLanguageCode: string;
  enabledLanguageCodes: string[];
  languages: Array<{ code: string; name: string; enabled: boolean }>;
  selectedLanguage: { code: string; enabled: boolean };
};

export type JobDashboardConfig = {
  version: 'new' | 'legacy';
};

export type CareersConfig = {
  jobAlerts: boolean;
};

export enum WorkflowAutomationsAllowedContext {
  DisqualifyReasons = 'disqualify_reasons',
  PipelineTemplates = 'pipeline_templates',
  Jobs = 'offers',
  JobTemplates = 'offer_templates',
}

export type WorkflowAutomationsConfig = {
  actions: AutomationActionType[];
  conditions: AutomationConditionType[];
  triggers: AutomationTriggerType[];
  contexts: WorkflowAutomationsAllowedContext[];
};

export type CandidateSalutationFieldsConfig = SalutationFieldsConfig;

export type ConfigsMap = {
  [FeatureCode.EEO]: EEOConfig;
  [FeatureCode.GDPRAutomations]: GDPRConfig;
  [FeatureCode.Locations]: LocationsConfig;
  [FeatureCode.Multilang]: MultilangConfig;
  [FeatureCode.CustomFields]: CustomFieldsConfig;
  [FeatureCode.JobDashboard]: JobDashboardConfig;
  [FeatureCode.Careers]: CareersConfig;
  [FeatureCode.WorkflowAutomations]: WorkflowAutomationsConfig;
  [FeatureCode.CandidateSalutationFields]: CandidateSalutationFieldsConfig;
};

export type FeatureSpec<T extends FeatureCode = FeatureCode> = {
  codename: T;
  configuration: T extends keyof ConfigsMap ? ConfigsMap[T] : EmptyConfig;
} & Base;
